import type { PageNoteView, PortalNoteView, Verdict, WidgetConfig, WidgetFeedbackCreated, WidgetFeedbackInput } from "@anota/shared";

/** Error de la API con el mensaje que manda el servidor (ya en lenguaje humano). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Casos esperados que el widget trata aparte (p. ej. "review_required"). */
    readonly code?: string
  ) {
    super(message);
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...init, mode: "cors", credentials: "omit" });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const error = (body as { error?: { message?: string; code?: string } } | null)?.error;
    throw new ApiError(res.status, error?.message ?? `HTTP ${res.status}`, error?.code);
  }
  return body as T;
}

export const getConfig = (base: string, key: string, review?: string) =>
  request<WidgetConfig>(`${base}/v1/widget/config?key=${encodeURIComponent(key)}${review ? `&review=${encodeURIComponent(review)}` : ""}`);

export const sendFeedback = (base: string, payload: WidgetFeedbackInput) =>
  request<WidgetFeedbackCreated>(`${base}/v1/widget/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export async function uploadScreenshot(upload: NonNullable<WidgetFeedbackCreated["upload"]>, blob: Blob): Promise<void> {
  const res = await fetch(upload.url, {
    method: "PUT",
    headers: { "Content-Type": blob.type, "X-Anota-Upload": upload.token },
    body: blob,
    mode: "cors",
    credentials: "omit",
  });
  if (!res.ok) throw new ApiError(res.status, `No se pudo subir la captura (HTTP ${res.status})`);
}

/** Las notas de esta página para quien la revisa (pines): pide el enlace de revisión. */
export const getPageNotes = (base: string, key: string, review: string, url: string) =>
  request<{ items: PageNoteView[] }>(
    `${base}/v1/widget/notes?key=${encodeURIComponent(key)}&review=${encodeURIComponent(review)}&url=${encodeURIComponent(url)}`
  );

export const replyToNote = (base: string, key: string, review: string, id: string, body: string) =>
  request<unknown>(`${base}/v1/widget/notes/${encodeURIComponent(id)}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectKey: key, reviewToken: review, body }),
  });

/** «Tus notas»: las de los accesos que guardó este navegador y, con enlace de revisión, las de ese enlace. */
export const getMine = (base: string, key: string, tokens: string[], review?: string) =>
  request<{ items: PortalNoteView[] }>(`${base}/v1/widget/mine`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectKey: key, tokens, ...(review ? { reviewToken: review } : {}) }),
  });

const post = (url: string, body: unknown) => request<unknown>(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

/** «¿Está bien así?» de una nota resuelta: desde «Tus notas» (`mine`) o desde los pines (`notes`, con el enlace de revisión). */
export const sendVerdict = (base: string, where: "mine" | "notes", id: string, body: { projectKey: string; token?: string; reviewToken?: string; verdict: Verdict; body?: string }) =>
  post(`${base}/v1/widget/${where}/${encodeURIComponent(id)}/verdict`, body);

/** El enlace a «Tus notas» para otro dispositivo (la clave de este navegador, con las notas de sus accesos). */
export const mineLink = (base: string, projectKey: string, key: string, tokens: string[]) =>
  request<{ url: string }>(`${base}/v1/widget/mine/key`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectKey, key, tokens }),
  });

export const replyToMine = (base: string, key: string, id: string, auth: { token?: string; reviewToken?: string }, body: string) =>
  request<unknown>(`${base}/v1/widget/mine/${encodeURIComponent(id)}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectKey: key, ...auth, body }),
  });
